import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CalendarIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { Button } from '../../ui/Button/Button';
import { Calendar } from '../../ui/Calendar/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/Popover/Popover';
import { Label } from '../../ui/Label';

export interface DateFieldProps {
  /**
   * Currently selected date. `null`/`undefined` show the placeholder.
   * Accepts both `Date` and ISO string for callers wired to react-hook-form
   * with a `Date | string | null` field.
   */
  value: Date | string | null | undefined;
  /**
   * Called with the picked `Date` (or `undefined` when cleared via the
   * Calendar's deselect). Callers that store ISO strings should
   * normalize in their `onChange` handler.
   */
  onChange: (date: Date | undefined) => void;
  /** Optional inline label rendered above the trigger. */
  label?: string;
  /** Mark the field as required (renders a `*` next to the label). */
  required?: boolean;
  /** Placeholder shown when no date is selected. Defaults to "Select date". */
  placeholder?: string;
  /** Disable the trigger and the popover. */
  disabled?: boolean;
  /** Inline error message rendered under the trigger. */
  error?: string;
  /**
   * Earliest allowed date (inclusive). Used by `<Calendar disabled>` to
   * grey out / lock prior days. Common pattern: pass `startsAt` to the
   * `endsAt` field so end-date can't precede start-date.
   */
  minDate?: Date;
  /** Latest allowed date (inclusive). Mirror of `minDate`. */
  maxDate?: Date;
  /** Format string for the trigger's display label. Defaults to `'PPP'`. */
  displayFormat?: string;
  /** Trigger className passthrough. */
  className?: string;
  /** id for label/aria wiring. */
  id?: string;
  /**
   * Close the popover automatically once the user picks a day. The
   * field is single-select so leaving the popover open after a pick
   * is almost always wrong — users have to click out by hand and
   * often think the click didn't register. Default `true`. Set to
   * `false` when the calendar is part of a range-picker UX that
   * needs sticky popover state.
   */
  autoCloseOnSelect?: boolean;
}

/**
 * `DateField` — Shared single-date picker for forms.
 *
 * Wraps the `Popover` + `Calendar` pattern in a labelled input-style
 * trigger so that every form across the unified dashboards
 * (`carrier-dashboard`, `customer-dashboard`, `admin-panel`) gets the
 * same date input treatment without each form re-implementing the
 * trigger styling.
 *
 * Pair with react-hook-form's `Controller` for form-managed state, or
 * call directly with controlled `value` / `onChange` for ad-hoc
 * usage (e.g. AgreementForm).
 */
export function DateField({
  value,
  onChange,
  label,
  required = false,
  placeholder,
  disabled = false,
  error,
  minDate,
  maxDate,
  displayFormat = 'PPP',
  className,
  id,
  autoCloseOnSelect = true,
}: DateFieldProps) {
  const { t } = useTranslation('c-main');
  const placeholderText = placeholder ?? t('date.select_date', { defaultValue: 'Select date' });

  const date = normalizeDate(value);
  const [open, setOpen] = useState(false);
  const disabledMatcher = (day: Date) => {
    if (minDate && day < startOfDay(minDate)) return true;
    if (maxDate && day > endOfDay(maxDate)) return true;
    return false;
  };

  const handleSelect = (day: Date | undefined) => {
    onChange(day);
    if (autoCloseOnSelect && day) setOpen(false);
  };

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-error ms-0.5">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-between text-start font-normal',
              !date && 'text-muted-foreground',
              error && 'border-error',
            )}>
            {date ? format(date, displayFormat) : <span>{placeholderText}</span>}
            <CalendarIcon className="w-4 h-4 ms-2" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            disabled={disabledMatcher}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {error && <p className="text-xs text-error">{error}</p>}
    </div>
  );
}

function normalizeDate(value: Date | string | null | undefined): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
